import { Box, CircularProgress, FormControlLabel, InputAdornment, Radio, TextField, Typography } from "@mui/material"
import { DeepKeys } from "@tanstack/react-form"
import { z as zod } from "zod"
import { useFormContext } from "connected-components/optimize-experiments/form-context"
import type { FormSections } from "connected-components/optimize-experiments/configuration-sections"
import OPTooltip from "components/material/tooltip"
import { ERROR } from "helpers/data-statuses"

import "./style.scss"

interface GoalRadioProps {
  value: string
  label: string
  fieldName?: DeepKeys<FormSections>
  selectedValue: string
  defaultValue: string
  status: string
  valueUnit: string
  validator?: zod.ZodNumber
}

export default function GoalRadio({
  value,
  label,
  fieldName,
  selectedValue,
  defaultValue,
  status,
  valueUnit,
  validator,
}: GoalRadioProps) {
  const form = useFormContext()
  const isSelected = selectedValue === value
  const isError = status === ERROR

  const radioControlLabel = (
      <FormControlLabel
        value={value}
        control={<Radio size="small" />}
        label={label}
        className="radio-option"
        disabled={isError && isSelected}
      />
  )

  return (
    <Box className="components_goal-radio">
      {isError && isSelected ? (
        <OPTooltip title="Unable to load total risk data. Please select a different option.">
          <span>{radioControlLabel}</span>
        </OPTooltip>
      ) : (
        radioControlLabel
      )}

      {isSelected && (
        <Box className="radio-container">
          <Box className="radio-row">
            <Box className="radio-current-value-container">
              <Typography className="value-label" variant="caption" color="text.secondary">Current</Typography>
              {status === "pending" ? (
                <CircularProgress className="loading" size="16px" color="primary" />
              ) : isError ? (
                <Typography variant="caption" color="text.secondary">
                  {defaultValue}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {defaultValue}{valueUnit}
                </Typography>
              )}
            </Box>
            {fieldName && (
              <Box className="radio-input-container">
                <Typography className="value-label" variant="caption" color="text.secondary">Value</Typography>
                <form.Field
                  name={fieldName}
                  validators={validator ? { onChange: validator, onMount: validator } : undefined}
                >
                  {(field) => (
                    <TextField
                      variant="standard"
                      size="small"
                      type="number"
                      value={field.state.value ?? ""}
                      onChange={(e) => field.handleChange(parseFloat(e.target.value))}
                      onBlur={field.handleBlur}
                      disabled={isError}
                      error={field.state.meta.errors.length > 0}
                      helperText={field.state.meta.errors[0]?.message}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position="end">{valueUnit}</InputAdornment>,
                        },
                      }}
                      className="radio-input"
                    />
                  )}
                </form.Field>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
